// How the home's catch-up band arranges itself (Figma "Catch Up states" 7419:47452).
//
// The band is ONE row of at most three slots, left→right:
//
//   [ jobs strip .............................. ] [ ideas pile ] [ connect tools ]
//
// and BELOW it the workspaces row (the user's spaces beside the Files entry — see `CatchUpSections`).
//
// The download-the-app QR used to be a slot here; the redesign moved it UP into the home header,
// beside the brief card (`DesktopHomeHeader`), so the band no longer carries it.
//
// The connect-tools callout is the band's THIRD slot only when the QR is being offered, because the
// two share the header's ⅓ column and only one can have it (Figma 15545:336847 draws exactly that
// state). Without the QR the callout takes the header slot instead and the band is the two-slot row it
// has always been — see `ConnectToolsCard`.
//
// Two independent rules:
//
//   • WORKSPACES — a plain ROW, always. Shown from ONE workspace on mobile web (matching the native
//     apps) and from TWO on the desktop, where a single-space row is redundant beside the header that
//     already names it. See `ROW_FROM_*` and `resolveHomeBandLayout`'s `narrow` argument.
//   • THE BAND — every slot is optional, and the row's TOTAL width follows whichever survive. The ideas
//     pile is a strict ⅓; the tasks strip sizes to its own CONTENT (capped at the remaining ⅔) and packs
//     to the start, so with one or two jobs the ideas pile sits RIGHT NEXT TO the strip rather than being
//     stranded at the far right. More jobs push the pile right until the row is full, after which the
//     strip scrolls inside its track.
//
//     With the connect slot present that content-sizing is dropped: three slots fill the row by
//     construction, so they take the design's fixed shares (`CONNECT_*`) — and the tasks strip PILES its
//     cards up rather than laying them end to end, which is what lets three of them read in the
//     narrower track (`PiledUpJobs`'s `piled`).

/** One third of the content width — the ideas pile's fixed column (Figma 405 of a 1280 row). */
const THIRD = 'calc((100% - 2 * var(--ds-space-32)) / 3)';

/**
 * The tasks track. `minmax(0, max-content)` sizes it to the cards (so a short strip pulls the ideas pile
 * left against it) while letting grid shrink it below that once the fixed ideas column needs the room —
 * past which `PiledUpJobs` scrolls inside it (`.row` is `overflow-x: auto`).
 */
const JOBS = 'minmax(0, max-content)';

/**
 * The three-slot row's shares, straight off Figma 15545:337586 — a 1280 row with two 32 gaps splits
 * 492 : 315 : 409. Written as `fr` ratios rather than pixels so the row still resolves at any pane
 * width; grid subtracts the gaps before dividing, so the proportions hold.
 */
const CONNECT_COLUMNS = 'minmax(0, 492fr) minmax(0, 315fr) minmax(0, 409fr)';

/** Rows where the connect slot is the only survivor besides one other: it keeps its ⅓. */
const CONNECT_THIRD = THIRD;

export interface HomeBandSlots {
  hasJobs: boolean;
  hasOpportunities: boolean;
  /** The connect-tools callout is riding in the band (the QR has the header slot). */
  hasConnect: boolean;
}

export interface HomeBandLayout {
  /** Whether the workspaces row renders at all. */
  showWorkspaces: boolean;
  /** Whether the band row renders at all. */
  showBand: boolean;
  /** The band's `grid-template-columns`, in slot order (jobs, ideas, connect). */
  columns: string;
}

/**
 * How many workspaces before the row is worth showing — and it differs by VIEWPORT, not by platform.
 *
 * A row listing one thing on a wide desktop is not a list — it is the thing, already named in the
 * header, wearing a row's chrome — so DESKTOP shows it only from TWO. A phone has no such header slot
 * and its whole screen is the list, so MOBILE WEB shows it from ONE, matching the native apps (iOS
 * `SymphonyHomeView` / Android `Pages` gate on a single workspace). So the row appears at one workspace
 * on iOS, Android and mobile web, and only at two on the desktop — the one deliberate outlier, because
 * it is the one surface with room to name the single space elsewhere. `resolveHomeBandLayout` takes the
 * viewport so the same account reads consistently across the three phone surfaces. See `orion-web/CLAUDE.md`.
 */
const ROW_FROM_DESKTOP = 2;
const ROW_FROM_MOBILE = 1;

/**
 * The band's columns for each surviving combination:
 *
 *   jobs + ideas + connect → the design's 492 : 315 : 409 (Figma 15545:337586)
 *   jobs + ideas           → tasks strip to its content, then the ideas pile's strict ⅓ (a full strip
 *                            lands on the design's ⅔ : ⅓; a short one lets the pile pull left against
 *                            it) — Figma 7419:47452
 *   jobs                   → full width, so the scroller's fade and chevrons sit at the page margins
 *   ideas                  → half the width — the pile must not stretch across the row (Figma "new
 *                            workspace" state)
 *
 * With the connect slot but no full band, whatever survives keeps its designed width and the callout
 * keeps a ⅓ beside it — never a slot stretched across the row.
 */
function bandColumns({ hasJobs, hasOpportunities, hasConnect }: HomeBandSlots): string {
  if (hasConnect) {
    if (hasJobs && hasOpportunities) return CONNECT_COLUMNS;
    if (hasJobs) return `${JOBS} ${CONNECT_THIRD}`;
    if (hasOpportunities) return `${THIRD} ${CONNECT_THIRD}`;
    return CONNECT_THIRD;
  }
  if (hasJobs && hasOpportunities) return `${JOBS} ${THIRD}`;
  if (hasJobs) return `minmax(0, 1fr)`;
  return `minmax(0, 50%)`;
}

/**
 * @param narrow the true VIEWPORT is mobile-web-narrow (`useNarrow`, 720) — NOT the docked-chat
 *   container squeeze. A desktop with the chat docked is still a desktop and keeps the ≥2 threshold.
 */
export function resolveHomeBandLayout(
  workspaceCount: number,
  slots: HomeBandSlots,
  narrow = false,
): HomeBandLayout {
  return {
    showWorkspaces: workspaceCount >= (narrow ? ROW_FROM_MOBILE : ROW_FROM_DESKTOP),
    showBand: slots.hasJobs || slots.hasOpportunities || slots.hasConnect,
    columns: bandColumns(slots),
  };
}
